import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from "recharts";

import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { API_URI } from "../../helpers";

/*
 * Rendimiento: ventas ÷ visitantes no alumnos.
 *
 * La tarjeta muestra cómo viene hoy HASTA LA ÚLTIMA HORA COMPLETA contra el promedio del
 * mismo día de la semana y del mismo mes del año. Al clickearla se abre el detalle con el
 * comportamiento por día, por mes y por hora, y los patrones que salen de ahí.
 *
 * TODO SE MUESTRA EN ÍNDICE: 100 = el promedio de todo el histórico. Los porcentajes
 * reales viven abajo del 1,5% y a esa escala no se leen — la diferencia entre 0,66% y
 * 0,90% no dice nada de un vistazo, pero 93 contra 127 sí. El "1 cada N" queda en el
 * texto chico y en el tooltip, para cuando hace falta el número concreto.
 *
 * Dos cuidados que se repiten en todo el archivo:
 *  - El índice puede venir null. Eso es "no hay dato" (GA4 todavía no importó esa franja),
 *    NO un 0: se muestra "—", nunca un cero, que se leería como que no se vendió nada.
 *  - Todo se compara contra la MISMA hora de corte. Mezclar el día de hoy a medias con
 *    días completos hace ver un derrumbe todas las mañanas.
 */
const idx = (v) => (v == null ? "—" : String(v));
const num = (v) => (v == null ? "—" : Math.round(v).toLocaleString("es-AR"));

const VERDE = "#16A34A";
const ROJO = "#DC2626";
const GRIS = "#94A3B8";
const AZUL = "#2563EB";
const CELESTE = "#7DD3FC";
const PALIDO = "#E2E8F0";

const FUERZA_COLOR = { alta: ROJO, media: "#CA8A04", baja: GRIS };

const NEUTRO = "#1f2937";

/**
 * Debajo de este desvío no se pinta nada: es el ruido de todos los días.
 *
 * Sin esta banda, un 94 contra 100 salía en rojo fuerte y alarmaba por un día que estaba
 * perfectamente bien. Con ~15 ventas diarias, cualquier cosa dentro del 10% es azar.
 */
const TOLERANCIA = 10;

/** Verde arriba, rojo abajo, y neutro cuando la diferencia no significa nada. */
function colorVs(valor, base, tolerancia = TOLERANCIA) {
  if (valor == null || base == null || base === 0) return NEUTRO;
  const d = ((valor - base) / base) * 100;
  if (Math.abs(d) < tolerancia) return NEUTRO;
  return d > 0 ? VERDE : ROJO;
}

/**
 * Cuánto se aparta de una referencia. Dos cuidados:
 *
 * La etiqueta NO es decorativa: el índice se mide contra el promedio general (100) y esta
 * flecha contra el día de la semana, así que sin decir contra qué compara quedaban dos
 * señales opuestas en la misma línea — un 106 en verde al lado de un ▼16% en rojo.
 *
 * Y con pocas ventas encima la flecha va en gris: a 3 ventas, mover una sola cambia el
 * número 33%, así que pintarlo de rojo sería alarmar por ruido.
 */
function Delta({ valor, base, contra, ventas }) {
  if (valor == null || base == null || base === 0) return null;
  const d = ((valor - base) / base) * 100;
  const up = d >= 0;
  const flojo = ventas != null && ventas < 5;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: flojo ? GRIS : colorVs(valor, base) }}>
      {up ? "▲" : "▼"} {Math.abs(d).toFixed(0)}% {contra}
    </span>
  );
}

/** Barras: la actual resaltada, y el resto según caiga arriba o abajo del promedio. */
const colorBarra = (x, esActual) => (esActual ? AZUL : (x.indice >= 100 ? CELESTE : PALIDO));

/** "Lunes" → "Lunes", "Domingo" → "Domingos". Sin esto salía "Luness". */
const plural = (dia) => (!dia ? "" : (dia.endsWith("s") ? dia : dia + "s"));

export default function Rendimiento() {
  const [d, setD] = useState(null);
  const [abierto, setAbierto] = useState(false);
  // El detalle permite mirar el día completo; la tarjeta va siempre con corte, que es la
  // única comparación honesta mientras el día está a medias.
  const [cutoff, setCutoff] = useState(true);
  const [cargando, setCargando] = useState(false);

  const traer = useCallback((corte) => {
    setCargando(true);
    fetch(`${API_URI}/dashboard/insights/performance?cutoff=${corte ? 1 : 0}`)
      .then((r) => r.json()).then(setD).catch(() => setD({ result: false }))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { traer(cutoff); }, [traer, cutoff]);

  if (!d) return null;
  if (!d.result) {
    return (
      <Card style={{ marginTop: 0 }}>
        <CardBody style={{ textAlign: "center", padding: "16px 8px", color: ROJO, fontSize: 13 }}>
          No se pudo calcular el rendimiento.
        </CardBody>
      </Card>
    );
  }

  const hoy = d.hoy || {};
  const base = (d.base && d.base.dia_reciente) || {};
  const mes = (d.base && d.base.mes) || {};

  return (
    <>
      <Card
        style={{ marginTop: 0, cursor: "pointer" }}
        onClick={() => setAbierto(true)}
        title="Ver el detalle por día de la semana, mes del año y hora"
      >
        <CardBody style={{ textAlign: "center", padding: "16px 8px" }}>
          {/* El color juzga contra el día de la semana, NO contra el 100. La pregunta de la
              tarjeta es "¿cómo viene hoy?", y para eso la vara es un lunes normal: contra el
              promedio general un lunes salía rojo por ser lunes, aunque estuviera bien. El
              "/100" sigue diciendo dónde cae en la escala general. */}
          <div style={{ fontSize: 26, fontWeight: 700,
                        color: hoy.ventas >= 5 ? colorVs(hoy.indice, base.tipico) : NEUTRO }}>
            {idx(hoy.indice)}
            <span style={{ fontSize: 13, fontWeight: 600, color: GRIS }}> / 100</span>
          </div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
            Rendimiento de hoy
          </div>
          <div style={{ marginTop: 3 }}>
            {/* Contra el domingo TÍPICO (mediana), no contra el promedio de los últimos
                ocho: ese promedio lo puede inflar un solo día de promo. */}
            <Delta valor={hoy.indice} base={base.tipico} ventas={hoy.ventas}
                   contra={`vs un ${hoy.dia && hoy.dia.toLowerCase()} normal`} />
          </div>
          <div style={{ fontSize: 11, color: GRIS, marginTop: 4, lineHeight: 1.5 }}>
            {/* "2 de 4" cuando el corte deja ventas afuera: al lado de la tarjeta de Ventas,
                un 2 suelto se lee como que se perdieron dos. */}
            {hoy.ventas_dia > hoy.ventas
              ? `${hoy.ventas} de ${hoy.ventas_dia} ventas`
              : `${hoy.ventas} ventas`}
            {" · "}{hoy.cada} visitantes · hasta {d.cutoff.label}
            <br />
            {plural(hoy.dia)} {idx(base.tipico)} · {hoy.mes} {idx(mes.indice)} · promedio 100
          </div>
          <div style={{ fontSize: 10, color: AZUL, marginTop: 6, fontWeight: 600 }}>ver detalle ▸</div>
        </CardBody>
      </Card>

      <Dialog open={abierto} onClose={() => setAbierto(false)} maxWidth="lg" fullWidth scroll="paper">
        <DialogContent style={{ padding: 20, background: "#F8FAFC" }}>
          <Detalle d={d} cutoff={cutoff} setCutoff={setCutoff} cargando={cargando} cerrar={() => setAbierto(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ detalle */

function Detalle({ d, cutoff, setCutoff, cargando, cerrar }) {
  const hoy = d.hoy || {};
  const base = (d.base && d.base.dia_reciente) || {};
  const hist = (d.base && d.base.dia_historico) || {};
  const mes = (d.base && d.base.mes) || {};
  const esp = d.esperado;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Rendimiento: ventas / visitantes no alumnos</span>
        <span style={{ flex: 1 }} />
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
               title="Recorta todos los días a la misma hora para comparar parejo. Sin esto, las referencias son de días enteros y hoy todavía va por la mitad.">
          <input type="checkbox" checked={cutoff} onChange={(e) => setCutoff(e.target.checked)} />
          hasta la hora actual{cutoff ? ` (${d.cutoff.label})` : ""}
        </label>
        <button onClick={cerrar} style={{ border: "none", background: "#E2E8F0", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontWeight: 600 }}>Cerrar</button>
      </div>

      {cutoff && (
        <div style={{ fontSize: 11, color: GRIS, marginTop: -8, marginBottom: 14, lineHeight: 1.5 }}>
          El corte llega hasta la última hora con visitas cargadas: GA4 procesa con un par de horas
          de retraso, así que las ventas más recientes todavía no tienen con qué compararse. Por eso
          acá pueden verse menos ventas que en la tarjeta de Ventas.
        </div>
      )}

      {cargando && <div style={{ padding: 8, color: GRIS, fontSize: 13 }}>Recalculando…</div>}

      {/* Comparación de hoy contra sus referencias. Sin corte, las referencias son de días
          ENTEROS y hoy va por la mitad: ahí la comparación deja de tener sentido y se dice,
          en vez de pintar de rojo un día que todavía no terminó. */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Caja titulo={`Hoy (${hoy.dia})`} valor={idx(hoy.indice)}
              sub={cutoff ? `${hoy.cada} · ${hoy.ventas} de ${hoy.ventas_dia} ventas del día`
                          : `${hoy.ventas_dia} ventas · el día todavía va por la mitad`}
              color={cutoff && hoy.ventas >= 5 ? colorVs(hoy.indice, base.tipico) : NEUTRO} />
        {/* El día TÍPICO es la mediana, no el promedio del tramo: ese lo puede inflar un
            solo día de promo y hace ver mal a un día normal. */}
        <Caja titulo={`${base.label} · típico`} valor={idx(base.tipico)} sub={`${base.dias} días · el conjunto rindió ${idx(base.indice)}`} />
        <Caja titulo={`${hist.label} · típico`} valor={idx(hist.tipico)} sub={`${hist.dias} días · el conjunto rindió ${idx(hist.indice)}`} />
        <Caja titulo={mes.label} valor={idx(mes.indice)} sub={`${mes.cada} · ${mes.dias} días`} />
      </div>

      <div style={{ fontSize: 12, color: GRIS, marginBottom: 16 }}>
        <strong>100 = el promedio de siempre</strong> ({d.promedio.cada} visitantes, sobre {d.promedio.dias} días).
        {" "}Arriba de 100 se vende mejor que lo habitual; abajo, peor — pero una diferencia
        {" "}de menos del {TOLERANCIA}% es el movimiento de todos los días, no una señal.
      </div>

      {esp && cutoff && (
        <div style={{ background: "#fff", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13, border: "1px solid #E2E8F0" }}>
          A esta hora, un {hoy.dia.toLowerCase()} normal tendría <strong>{esp.ventas}</strong> ventas con este tráfico
          {" "}(hoy: <strong>{hoy.ventas}</strong>, {esp.diferencia >= 0 ? "+" : ""}{esp.diferencia}).
          {" "}Visitantes: {num(hoy.visitantes)} vs {num(esp.visitantes_base)} habituales. <Causa causa={esp.causa} />
        </div>
      )}

      {!cutoff && (
        <div style={{ background: "#FEF9C3", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13, border: "1px solid #FDE68A" }}>
          Con el corte apagado las referencias son de <strong>días enteros</strong>, así que no se
          pueden comparar contra hoy, que todavía va por la mitad. Sirve para leer los gráficos de
          abajo con el día completo; para saber cómo viene hoy, volvé a prender el corte.
        </div>
      )}

      {/* Patrones */}
      {(d.patrones || []).length > 0 && (
        <div style={{ background: "#fff", borderRadius: 6, padding: 14, marginBottom: 16, border: "1px solid #E2E8F0" }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Patrones para vender mejor</div>
          {d.patrones.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 0", borderBottom: i < d.patrones.length - 1 ? "1px solid #F1F5F9" : "none" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: FUERZA_COLOR[p.fuerza] || GRIS, borderRadius: 4, padding: "2px 6px", minWidth: 46, textAlign: "center", marginTop: 2 }}>
                {p.fuerza}
              </span>
              <div style={{ flex: 1, fontSize: 13 }}>
                <strong>{p.titulo}</strong> — {p.detalle}
              </div>
            </div>
          ))}
        </div>
      )}

      <Grafico titulo="Por día de la semana" nota="Todo el histórico junto. La línea del 100 es el promedio.">
        <BarChart data={d.por_dia}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
          <XAxis dataKey="label" tickFormatter={(v) => v.substring(0, 3)} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
          <Tooltip content={<TT />} />
          <ReferenceLine y={100} stroke={GRIS} strokeDasharray="4 4" />
          <Bar dataKey="indice" radius={[3, 3, 0, 0]}>
            {d.por_dia.map((x, i) => <Cell key={i} fill={colorBarra(x, x.label === hoy.dia)} />)}
          </Bar>
        </BarChart>
      </Grafico>

      <Grafico titulo="Por mes del año" nota="Todos los años juntos. Es la variación más grande que hay en el negocio.">
        <BarChart data={d.por_mes}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
          <XAxis dataKey="label" tickFormatter={(v) => v.substring(0, 3)} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
          <Tooltip content={<TT />} />
          <ReferenceLine y={100} stroke={GRIS} strokeDasharray="4 4" />
          <Bar dataKey="indice" radius={[3, 3, 0, 0]}>
            {d.por_mes.map((x, i) => <Cell key={i} fill={colorBarra(x, x.label === hoy.mes)} />)}
          </Bar>
        </BarChart>
      </Grafico>

      <Grafico titulo="Por hora del día" nota="Siempre sobre el día completo y todo el histórico: es el perfil horario, no la foto de hoy.">
        <BarChart data={d.por_hora}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
          <XAxis dataKey="hora" tickFormatter={(v) => v + "h"} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
          <Tooltip content={<TT hora />} />
          <ReferenceLine y={100} stroke={GRIS} strokeDasharray="4 4" />
          <Bar dataKey="indice" radius={[3, 3, 0, 0]}>
            {d.por_hora.map((x, i) => <Cell key={i} fill={colorBarra(x, false)} />)}
          </Bar>
        </BarChart>
      </Grafico>

      <Grafico titulo="Últimos 60 días" nota={`Rendimiento diario${cutoff ? " hasta la misma hora" : ""}, contra el promedio (100).`}>
        <LineChart data={d.recientes}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
          <XAxis dataKey="date" tickFormatter={(v) => v.substring(5)} tick={{ fontSize: 10 }} minTickGap={24} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
          <Tooltip content={<TT />} />
          <ReferenceLine y={100} stroke={GRIS} strokeDasharray="4 4" />
          <Line type="monotone" dataKey="indice" stroke={AZUL} strokeWidth={2} dot={false} connectNulls={false} />
        </LineChart>
      </Grafico>

      {d.cobertura && (
        <div style={{ fontSize: 11, color: GRIS, marginTop: 4, lineHeight: 1.6 }}>
          Base: {d.cobertura.dias} días desde {d.cobertura.desde}. {d.cobertura.nota}
          {" "}Las ventas salen de la base; los visitantes, de GA4 — por eso este porcentaje no es
          el mismo que la conversión sobre intentos de checkout del resumen.
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ piezas */

function Caja({ titulo, valor, sub, color }) {
  return (
    <div style={{ flex: "1 1 170px", background: "#fff", borderRadius: 6, padding: "10px 14px", border: "1px solid #E2E8F0" }}>
      <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#1f2937" }}>{valor}</div>
      <div style={{ fontSize: 11, color: GRIS }}>{sub}</div>
    </div>
  );
}

function Causa({ causa }) {
  const T = {
    menos_trafico: ["Viene menos gente que de costumbre: el problema está arriba del embudo, no en la página.", "#CA8A04"],
    mas_trafico: ["Está entrando más gente que de costumbre.", VERDE],
    peor_conversion: ["Entra gente parecida pero compra menos: mirá la página y el precio, no las campañas.", ROJO],
    mejor_conversion: ["Con el mismo tráfico, está comprando más gente.", VERDE],
    normal: ["Tráfico y conversión están dentro de lo normal.", GRIS],
    sin_dato: ["", GRIS],
  };
  const [txt, color] = T[causa] || ["", GRIS];
  if (!txt) return null;
  return <span style={{ color, fontWeight: 600 }}>{txt}</span>;
}

function Grafico({ titulo, nota, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 6, padding: 14, marginBottom: 14, border: "1px solid #E2E8F0" }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{titulo}</div>
      {nota && <div style={{ fontSize: 11, color: GRIS, marginBottom: 8 }}>{nota}</div>}
      <ResponsiveContainer width="100%" height={190}>{children}</ResponsiveContainer>
    </div>
  );
}

/**
 * Tooltip propio. El índice es para leer la forma del gráfico; acá abajo va el número
 * concreto ("1 cada N") y de qué muestra sale, que es lo que decide si creerle.
 */
function TT({ active, payload, label, hora }) {
  if (!active || !payload || !payload.length) return null;
  const x = payload[0].payload;
  const dif = x.indice == null ? null : x.indice - 100;
  return (
    <div style={{ background: "#fff", border: "1px solid #CBD5E1", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{hora ? label + ":00" : x.label || label}</div>
      <div>
        <strong>{idx(x.indice)}</strong>
        {dif != null && (
          <span style={{ color: dif >= 0 ? VERDE : ROJO, marginLeft: 6 }}>
            {dif >= 0 ? "+" : ""}{dif}% vs promedio
          </span>
        )}
      </div>
      <div style={{ color: GRIS }}>{x.cada} visitantes</div>
      <div style={{ color: GRIS }}>{num(x.ventas)} ventas / {num(x.visitantes)} visitantes</div>
      {x.dias != null && <div style={{ color: GRIS }}>{x.dias} días</div>}
      {x.confiable === false && <div style={{ color: "#CA8A04" }}>muestra chica</div>}
    </div>
  );
}
